/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T01:15:00+05:00
Intent: Turn a campaign's audit records (action 2464) into one Excel sheet: one row per attempt, with its outcome, answers, verification method, source, confidence and proof summary. exceljs is loaded on demand through loadExcelWorkbook, which works in the production build too.
*/

import { loadExcelWorkbook, type SurveyCell } from './survey-export.util';

/** One attempt as actions 2463 / 2464 return it. */
export interface AuditRecord {
  attempt_id: string;
  participant_id: string | null;
  participant_name: string;
  email: string;
  phone_number: string;
  level_id: string;
  level_name: string;
  level_name_ar: string;
  level_position: number | null;
  level_type: string;
  attempt_no: number | null;
  status: string;
  reason_code: string | null;
  reason: string | null;
  started_at: string | null;
  submitted_at: string | null;
  verification_method: string | null;
  source: string | null;
  confidence: number | null;
  confidence_formula: string | null;
  verified_at: string | null;
  proof: Record<string, unknown> | null;
  flagged: boolean;
  answers: AuditAnswer[];
  answers_withheld: string | null;
}

export interface AuditAnswer {
  question_id: string | null;
  question_text?: string | null;
  arabic_question_text?: string | null;
  option_id?: string | null;
  option_text?: string | null;
  arabic_option_text?: string | null;
  answer_text?: string | null;
  is_correct?: boolean | null;
  answered_at?: string | null;
}

export interface AuditRecordsPage {
  items: AuditRecord[];
  total: number;
  page: number;
  per_page: number;
  /** false until the verified-action records exist on the server (migrations 045/046). */
  ledger: boolean;
  truncated: boolean;
}

export type AuditLang = 'en' | 'ar';

/** Reads the 2463 / 2464 response, whatever envelope it arrives in. */
export function readAuditPage(response: any): AuditRecordsPage {
  const body = response?.items ? response : (response?.data?.items ? response.data : {});
  const items: AuditRecord[] = Array.isArray(body.items) ? body.items : [];
  return {
    items,
    total: Number(body.total ?? items.length) || 0,
    page: Number(body.page ?? 1) || 1,
    per_page: Number(body.per_page ?? items.length) || items.length,
    ledger: body.ledger !== false,
    truncated: body.truncated === true,
  };
}

const pick = (lang: AuditLang, en?: string | null, ar?: string | null) =>
  String((lang === 'ar' ? ar || en : en || ar) ?? '').trim();

/** The level as the marketer named it, in the dashboard language. */
export function auditLevelName(record: AuditRecord, lang: AuditLang): string {
  return pick(lang, record.level_name, record.level_name_ar);
}

/** "Question: answer" for each answer, in the dashboard language. Several options for one question are joined. */
export function auditAnswerLines(record: AuditRecord, lang: AuditLang): string[] {
  const byQuestion = new Map<string, { question: string; values: string[] }>();
  (record.answers || []).forEach((answer, index) => {
    const key = answer.question_id ?? `#${index}`;
    const value = pick(lang, answer.option_text, answer.arabic_option_text) || String(answer.answer_text ?? '').trim();
    const entry = byQuestion.get(key) ?? { question: pick(lang, answer.question_text, answer.arabic_question_text), values: [] };
    if (value && !entry.values.includes(value)) entry.values.push(value);
    byQuestion.set(key, entry);
  });
  return [...byQuestion.values()].map(({ question, values }) =>
    question ? `${question}: ${values.join(', ')}` : values.join(', '));
}

/** The proof summary as "key: value" pairs. */
export function auditProofText(proof: Record<string, unknown> | null): string {
  if (!proof) return '';
  return Object.entries(proof)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
    .join('; ');
}

export interface AuditSheetLabels {
  sheet: string;
  headers: string[];
  status: (code: string) => string;
  method: (code: string | null) => string;
  source: (code: string | null) => string;
  withheld: (mode: string) => string;
  yes: string;
  no: string;
}

const asDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** The export's rows, in the order of the 20 headers in AuditSheetLabels. */
export function auditRows(records: AuditRecord[], lang: AuditLang, labels: AuditSheetLabels): SurveyCell[][] {
  return records.map(record => [
    record.participant_id ?? '',
    record.participant_name || '',
    record.phone_number || '',
    record.email || '',
    auditLevelName(record, lang),
    record.level_type || '',
    record.attempt_no,
    labels.status(record.status),
    record.reason_code ?? '',
    record.reason ?? '',
    asDate(record.started_at),
    asDate(record.submitted_at),
    labels.method(record.verification_method),
    labels.source(record.source),
    record.confidence,
    record.confidence_formula ?? '',
    asDate(record.verified_at),
    record.flagged ? labels.yes : labels.no,
    record.answers_withheld ? labels.withheld(record.answers_withheld) : auditAnswerLines(record, lang).join(' | '),
    auditProofText(record.proof),
  ]);
}

export async function writeAuditWorkbook(
  records: AuditRecord[],
  lang: AuditLang,
  labels: AuditSheetLabels,
): Promise<ArrayBuffer> {
  const Workbook = await loadExcelWorkbook();
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet(labels.sheet, { views: [{ rightToLeft: lang === 'ar', state: 'frozen', ySplit: 1 }] });
  sheet.addRow(labels.headers);
  const header = sheet.getRow(1);
  header.font = { bold: true, color: { argb: 'FF101828' } };
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F4F7' } };
  auditRows(records, lang, labels).forEach(row => sheet.addRow(row));
  [38, 24, 18, 26, 24, 14, 9, 14, 18, 36, 18, 18, 20, 12, 11, 16, 18, 9, 60, 48]
    .forEach((width, index) => sheet.getColumn(index + 1).width = width);
  [11, 12, 17].forEach(column => sheet.getColumn(column).numFmt = 'dd mmm yyyy hh:mm');
  sheet.getColumn(15).numFmt = '0.00';
  [10, 19, 20].forEach(column => sheet.getColumn(column).alignment = { wrapText: true, vertical: 'top' });
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: labels.headers.length } };
  return workbook.xlsx.writeBuffer() as Promise<ArrayBuffer>;
}

export function auditExportFileName(campaignName: string, date: Date = new Date()): string {
  const name = String(campaignName ?? '').replace(/[\\/:*?"<>|\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim();
  const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return `Audit Records - ${name ? `${name} - ` : ''}${day}.xlsx`;
}
